/**
 * Hand-rolled layered (Sugiyama-style) DAG layout.
 *
 * No external graph library, no CDN. Four phases, the classic ones:
 *
 *   1. cycle removal      — DFS; back edges are reversed and flagged
 *   2. layering           — longest-path ranking on the acyclic graph
 *   3. ordering           — dummy nodes for long edges, then weighted-median
 *                           sweeps up and down to cut crossings
 *   4. coordinates        — priority/median x-placement inside each layer
 *
 * Edge semantics from the API (`todo_api/routers/graph.py`):
 *   - `blocks`  : source blocks target  → source is drawn ABOVE target
 *   - `parent`  : source's epic is target → target (the epic) is ABOVE source
 */

export const NODE_W = 176
export const NODE_H = 46
export const LAYER_GAP = 92
export const NODE_GAP = 26

/**
 * Widest a single row of boxes may get before it is wrapped.
 *
 * 1,800 px is about 9 boxes. Combined with component packing this holds the
 * whole drawing near a 17:10 aspect, so "Fit" scales to roughly 0.25 instead of
 * the 0.03 sliver an unbounded single row produced.
 */
export const MAX_ROW_WIDTH = 1800

/**
 * Normalise API edges into `{ from, to, type, id }` where `from` is drawn above
 * `to`. Edges whose endpoints are missing from `nodes` are dropped.
 */
export function normalizeEdges(nodes, edges) {
  const known = new Set(nodes.map((n) => n.id))
  const out = []
  for (const e of edges || []) {
    if (!known.has(e.source) || !known.has(e.target)) continue
    if (e.source === e.target) continue
    if (e.type === 'parent') out.push({ id: e.id, from: e.target, to: e.source, type: 'parent' })
    else out.push({ id: e.id, from: e.source, to: e.target, type: e.type || 'blocks' })
  }
  // De-duplicate identical from→to pairs of the same type.
  const seen = new Set()
  return out.filter((e) => {
    const k = `${e.from}>${e.to}:${e.type}`
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
}

/**
 * Break cycles by reversing DFS back edges.
 * Returns `{ edges, reversed }` — `edges` is acyclic, each carrying `reversed`.
 */
export function breakCycles(nodeIds, edges) {
  const out = new Map()
  for (const id of nodeIds) out.set(id, [])
  for (const e of edges) out.get(e.from).push(e)

  const WHITE = 0
  const GREY = 1
  const BLACK = 2
  const color = new Map(nodeIds.map((id) => [id, WHITE]))
  const result = []
  let reversedCount = 0

  for (const root of nodeIds) {
    if (color.get(root) !== WHITE) continue
    // Iterative DFS so a deep chain cannot blow the JS stack.
    const stack = [{ id: root, i: 0 }]
    color.set(root, GREY)
    while (stack.length) {
      const frame = stack[stack.length - 1]
      const adj = out.get(frame.id)
      if (frame.i >= adj.length) {
        color.set(frame.id, BLACK)
        stack.pop()
        continue
      }
      const e = adj[frame.i++]
      const c = color.get(e.to)
      if (c === GREY) {
        result.push({ ...e, from: e.to, to: e.from, reversed: true })
        reversedCount += 1
      } else {
        result.push({ ...e, reversed: false })
        if (c === WHITE) {
          color.set(e.to, GREY)
          stack.push({ id: e.to, i: 0 })
        }
      }
    }
  }
  return { edges: result, reversed: reversedCount }
}

/** Longest-path layering: rank(v) = 1 + max(rank(preds)). */
export function assignLayers(nodeIds, edges) {
  const preds = new Map(nodeIds.map((id) => [id, []]))
  const succs = new Map(nodeIds.map((id) => [id, []]))
  const indeg = new Map(nodeIds.map((id) => [id, 0]))
  for (const e of edges) {
    preds.get(e.to).push(e.from)
    succs.get(e.from).push(e.to)
    indeg.set(e.to, indeg.get(e.to) + 1)
  }
  const rank = new Map(nodeIds.map((id) => [id, 0]))
  const queue = nodeIds.filter((id) => indeg.get(id) === 0)
  let head = 0
  while (head < queue.length) {
    const id = queue[head++]
    for (const s of succs.get(id)) {
      rank.set(s, Math.max(rank.get(s), rank.get(id) + 1))
      indeg.set(s, indeg.get(s) - 1)
      if (indeg.get(s) === 0) queue.push(s)
    }
  }
  return rank
}

/** Weighted median of a sorted numeric array; -1 when empty (keep in place). */
function median(values) {
  if (!values.length) return -1
  const v = [...values].sort((a, b) => a - b)
  const m = Math.floor(v.length / 2)
  if (v.length % 2 === 1) return v[m]
  if (v.length === 2) return (v[0] + v[1]) / 2
  const left = v[m - 1] - v[0]
  const right = v[v.length - 1] - v[m]
  return (v[m - 1] * right + v[m] * left) / (left + right || 1)
}

/** Count edge crossings between two adjacent, ordered layers. */
function crossings(layerA, layerB, adjacency) {
  const posB = new Map(layerB.map((id, i) => [id, i]))
  const seq = []
  for (const a of layerA) {
    const targets = (adjacency.get(a) || [])
      .map((b) => posB.get(b))
      .filter((x) => x !== undefined)
      .sort((x, y) => x - y)
    seq.push(...targets)
  }
  let count = 0
  for (let i = 0; i < seq.length; i += 1) {
    for (let j = i + 1; j < seq.length; j += 1) if (seq[i] > seq[j]) count += 1
  }
  return count
}

/**
 * Lay out ONE weakly connected component with the layered algorithm.
 *
 * Callers want :func:`layoutGraph`, which packs the components this produces.
 *
 * @param {Array<{id:number}>} nodes  API graph nodes.
 * @param {Array<object>} apiEdges    API graph edges.
 * @returns {{nodes:Array, edges:Array, width:number, height:number,
 *            layers:number, widest:number, reversed:number}}
 *   Positioned nodes (`x`,`y` = top-left of the box) and edges carrying a
 *   `points` polyline through their dummy bends.
 */
export function layoutComponent(nodes, apiEdges, maxRowWidth = MAX_ROW_WIDTH) {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const nodeIds = nodes.map((n) => n.id)
  const norm = normalizeEdges(nodes, apiEdges)
  const { edges: acyclic, reversed } = breakCycles(nodeIds, norm)
  const rank = assignLayers(nodeIds, acyclic)

  // --- dummy nodes so every edge spans exactly one layer -------------------
  const layers = []
  const place = (id, r) => {
    while (layers.length <= r) layers.push([])
    layers[r].push(id)
  }
  const virt = new Map() // dummy id -> { rank, edge }
  for (const id of nodeIds) place(id, rank.get(id))

  const segments = [] // { from, to, edge }
  let dummySeq = 0
  for (const e of acyclic) {
    const r0 = rank.get(e.from)
    const r1 = rank.get(e.to)
    if (r1 - r0 <= 1) {
      segments.push({ from: e.from, to: e.to, edge: e })
      continue
    }
    let prev = e.from
    const chain = []
    for (let r = r0 + 1; r < r1; r += 1) {
      const did = `d${dummySeq++}`
      virt.set(did, { rank: r, edge: e })
      place(did, r)
      chain.push(did)
      segments.push({ from: prev, to: did, edge: e })
      prev = did
    }
    segments.push({ from: prev, to: e.to, edge: e })
    e.chain = chain
  }

  // --- ordering: median sweeps --------------------------------------------
  const down = new Map() // id -> ids in next layer
  const up = new Map() // id -> ids in previous layer
  for (const s of segments) {
    if (!down.has(s.from)) down.set(s.from, [])
    if (!up.has(s.to)) up.set(s.to, [])
    down.get(s.from).push(s.to)
    up.get(s.to).push(s.from)
  }

  let order = layers.map((l) => [...l])
  let best = order.map((l) => [...l])
  const totalCrossings = (ord) => {
    let n = 0
    for (let i = 0; i + 1 < ord.length; i += 1) n += crossings(ord[i], ord[i + 1], down)
    return n
  }
  let bestScore = totalCrossings(order)

  for (let pass = 0; pass < 8 && bestScore > 0; pass += 1) {
    const downward = pass % 2 === 0
    if (downward) {
      for (let r = 1; r < order.length; r += 1) {
        const pos = new Map(order[r - 1].map((id, i) => [id, i]))
        order[r] = sortByMedian(order[r], up, pos)
      }
    } else {
      for (let r = order.length - 2; r >= 0; r -= 1) {
        const pos = new Map(order[r + 1].map((id, i) => [id, i]))
        order[r] = sortByMedian(order[r], down, pos)
      }
    }
    const score = totalCrossings(order)
    if (score < bestScore) {
      bestScore = score
      best = order.map((l) => [...l])
    }
  }
  order = best

  // --- wrap over-wide layers into sub-rows ---------------------------------
  // One epic with 40 children is a single layer 8,000 px wide, which is what
  // makes "Fit" unreadable. A layer that no long edge passes through can be
  // wrapped without breaking the drawing: every edge touching it spans exactly
  // one layer, so it still reads top-to-bottom. Layers containing dummy nodes
  // are left alone — wrapping those would bend edges through the wrong rows.
  const widthOf = (id) => (virt.has(id) ? 1 : NODE_W)
  const perRow = Math.max(1, Math.floor(maxRowWidth / (NODE_W + NODE_GAP)))
  const rows = []
  for (let r = 0; r < order.length; r += 1) {
    const layer = order[r]
    const layerWidth = layer.length * (NODE_W + NODE_GAP)
    if (layerWidth <= maxRowWidth) {
      rows.push(layer)
      continue
    }
    // Dummies (the bends of long edges) all stay in the first sub-row, so a
    // long edge keeps a single consistent row to pass through.
    const dummies = layer.filter((id) => virt.has(id))
    const real = layer.filter((id) => !virt.has(id))
    let first = true
    for (let i = 0; i < real.length; i += perRow) {
      const chunk = real.slice(i, i + perRow)
      rows.push(first ? [...dummies, ...chunk] : chunk)
      first = false
    }
    if (first) rows.push(dummies)
  }
  const rowIndex = new Map()
  for (let i = 0; i < rows.length; i += 1) for (const id of rows[i]) rowIndex.set(id, i)

  // --- x coordinates: median alignment, then collision resolution ----------
  const x = new Map()
  for (const layer of rows) {
    let cursor = 0
    for (const id of layer) {
      x.set(id, cursor)
      cursor += widthOf(id) + NODE_GAP
    }
  }
  for (let pass = 0; pass < 4; pass += 1) {
    const downward = pass % 2 === 0
    const range = downward
      ? [...rows.keys()].slice(1)
      : [...rows.keys()].slice(0, -1).reverse()
    for (const r of range) {
      const neighbours = downward ? up : down
      const layer = rows[r]
      const desired = layer.map((id) => {
        const ns = (neighbours.get(id) || []).map((n) => x.get(n) + widthOf(n) / 2)
        const m = median(ns)
        return m < 0 ? x.get(id) + widthOf(id) / 2 : m
      })
      // Left-to-right sweep honouring minimum separation, then a right-to-left
      // sweep so nodes pushed right do not drag the whole layer.
      let cursor = -Infinity
      for (let i = 0; i < layer.length; i += 1) {
        const w = widthOf(layer[i])
        const want = desired[i] - w / 2
        const put = Math.max(want, cursor)
        x.set(layer[i], put)
        cursor = put + w + NODE_GAP
      }
      let rcursor = Infinity
      for (let i = layer.length - 1; i >= 0; i -= 1) {
        const w = widthOf(layer[i])
        const want = Math.min(x.get(layer[i]), rcursor - w)
        const put = Math.max(want, desired[i] - w / 2 - 400)
        x.set(layer[i], put)
        rcursor = put - NODE_GAP
      }
    }
  }

  // --- compaction: bound the gaps the median passes opened -----------------
  // The median/priority sweeps can leave a row far wider than the boxes in it
  // (a 33-box row measured 12,058 px). Preserve the order they chose, but cap
  // any single gap: width is what decides whether "Fit" is readable.
  const MAX_GAP = NODE_GAP * 3
  for (const layer of rows) {
    const sorted = [...layer].sort((a, b) => x.get(a) - x.get(b))
    let cursor = null
    for (let i = 0; i < sorted.length; i += 1) {
      const id = sorted[i]
      if (cursor === null) {
        cursor = x.get(id)
      } else {
        const gap = Math.min(Math.max(x.get(id) - cursor, NODE_GAP), MAX_GAP)
        cursor += gap
        x.set(id, cursor)
      }
      cursor += widthOf(id)
    }
  }

  // Normalise so the leftmost coordinate is 0.
  let minX = Infinity
  let maxX = -Infinity
  for (const layer of rows) {
    for (const id of layer) {
      minX = Math.min(minX, x.get(id))
      maxX = Math.max(maxX, x.get(id) + widthOf(id))
    }
  }
  if (!Number.isFinite(minX)) {
    minX = 0
    maxX = 0
  }
  const yOf = (r) => r * (NODE_H + LAYER_GAP)

  const placed = []
  for (let r = 0; r < rows.length; r += 1) {
    for (const id of rows[r]) {
      if (virt.has(id)) continue
      placed.push({ ...byId.get(id), x: x.get(id) - minX, y: yOf(r), rank: rank.get(id) })
    }
  }
  const point = (id) => ({
    x: x.get(id) - minX + widthOf(id) / 2,
    y: yOf(rowIndex.get(id)) + (virt.has(id) ? 0 : NODE_H / 2),
  })

  const outEdges = acyclic.map((e) => {
    const chain = e.chain || []
    const pts = [point(e.from), ...chain.map(point), point(e.to)]
    // Anchor the visible ends on the box borders, not the centres.
    pts[0] = { x: pts[0].x, y: yOf(rowIndex.get(e.from)) + NODE_H }
    pts[pts.length - 1] = { x: pts[pts.length - 1].x, y: yOf(rowIndex.get(e.to)) }
    return {
      id: e.id,
      type: e.type,
      reversed: !!e.reversed,
      source: e.reversed ? e.to : e.from,
      target: e.reversed ? e.from : e.to,
      from: e.from,
      to: e.to,
      points: pts,
    }
  })

  return {
    nodes: placed,
    edges: outEdges,
    width: Math.max(maxX - minX, NODE_W),
    height: Math.max(rows.length * (NODE_H + LAYER_GAP) - LAYER_GAP, NODE_H),
    layers: order.length,
    widest: rows.reduce((m, l) => Math.max(m, l.filter((id) => !virt.has(id)).length), 0),
    reversed,
  }
}

/** Horizontal and vertical gutter between packed components. */
export const GROUP_GAP = 64

/** Aspect ratio the packer aims for (width / height) — roughly a laptop viewport. */
export const TARGET_ASPECT = 1.7

/**
 * Lay out a whole graph by laying out each connected component and packing them.
 *
 * WHY THIS EXISTS. The layered algorithm alone puts every rank-0 node in one
 * row. Real tracker data is mostly a shallow forest — 203 nodes, 121 edges,
 * dozens of them isolated — so rank 0 held ~100 boxes and the drawing came out
 * about 20,000 px wide and 500 px tall. Fitted into a 1236x640 viewport that is
 * a 0.03 scale factor: one illegible strip across the top with 80% of the
 * canvas empty. It was not a density problem — 41 nodes with 4 edges produced
 * the identical strip, because 37 of them were isolated rank-0 roots.
 *
 * So: lay out each weakly connected component on its own (where layering is
 * meaningful and the component is naturally narrow), then shelf-pack the
 * component boxes towards a ~17:10 aspect ratio. Isolated singletons pack into
 * a dense grid instead of a 200-wide row each.
 *
 * @param {Array<{id:number}>} nodes  API graph nodes.
 * @param {Array<object>} apiEdges    API graph edges.
 * @returns {{nodes:Array, edges:Array, width:number, height:number,
 *            layers:number, widest:number, reversed:number, groups:number}}
 */
export function layoutGraph(nodes, apiEdges) {
  const list = nodes || []
  if (!list.length) {
    return {
      nodes: [], edges: [], width: NODE_W, height: NODE_H,
      layers: 0, widest: 0, reversed: 0, groups: 0,
    }
  }
  const components = connectedComponents(list, apiEdges)
  const boxes = components.map((group) => {
    const laid = layoutComponent(group, apiEdges)
    return { laid, w: laid.width, h: laid.height }
  })

  // Shelf packing, tallest shelf first, towards TARGET_ASPECT.
  const area = boxes.reduce((a, b) => a + (b.w + GROUP_GAP) * (b.h + GROUP_GAP), 0)
  const widest = boxes.reduce((m, b) => Math.max(m, b.w), 0)
  const targetWidth = Math.max(widest, Math.sqrt(area * TARGET_ASPECT))

  const ordered = [...boxes].sort((a, b) => b.h - a.h || b.w - a.w)
  let cursorX = 0
  let cursorY = 0
  let shelfH = 0
  let maxX = 0
  for (const box of ordered) {
    if (cursorX > 0 && cursorX + box.w > targetWidth) {
      cursorY += shelfH + GROUP_GAP
      cursorX = 0
      shelfH = 0
    }
    box.dx = cursorX
    box.dy = cursorY
    cursorX += box.w + GROUP_GAP
    maxX = Math.max(maxX, cursorX - GROUP_GAP)
    shelfH = Math.max(shelfH, box.h)
  }
  const totalH = cursorY + shelfH

  const outNodes = []
  const outEdges = []
  let reversed = 0
  let layers = 0
  let widestLayer = 0
  for (const box of ordered) {
    reversed += box.laid.reversed
    layers = Math.max(layers, box.laid.layers)
    widestLayer = Math.max(widestLayer, box.laid.widest)
    for (const n of box.laid.nodes) outNodes.push({ ...n, x: n.x + box.dx, y: n.y + box.dy })
    for (const e of box.laid.edges) {
      outEdges.push({
        ...e,
        points: e.points.map((pt) => ({ x: pt.x + box.dx, y: pt.y + box.dy })),
      })
    }
  }
  return {
    nodes: outNodes,
    edges: outEdges,
    width: Math.max(maxX, NODE_W),
    height: Math.max(totalH, NODE_H),
    layers,
    widest: widestLayer,
    reversed,
    groups: boxes.length,
  }
}

/** Reorder one layer by the median position of its neighbours. */
function sortByMedian(layer, neighbours, pos) {
  const keyed = layer.map((id, i) => {
    const ns = (neighbours.get(id) || []).map((n) => pos.get(n)).filter((p) => p !== undefined)
    return { id, i, m: median(ns) }
  })
  keyed.sort((a, b) => {
    if (a.m < 0 && b.m < 0) return a.i - b.i
    if (a.m < 0) return -1
    if (b.m < 0) return 1
    return a.m - b.m || a.i - b.i
  })
  return keyed.map((k) => k.id)
}

/**
 * Split a graph into weakly connected components, largest first.
 * Used by the grouped-tree fallback when the DAG is too big to read.
 */
export function connectedComponents(nodes, apiEdges) {
  const edges = normalizeEdges(nodes, apiEdges)
  const parent = new Map(nodes.map((n) => [n.id, n.id]))
  const find = (a) => {
    let r = a
    while (parent.get(r) !== r) r = parent.get(r)
    while (parent.get(a) !== r) {
      const next = parent.get(a)
      parent.set(a, r)
      a = next
    }
    return r
  }
  for (const e of edges) {
    const ra = find(e.from)
    const rb = find(e.to)
    if (ra !== rb) parent.set(ra, rb)
  }
  const groups = new Map()
  for (const n of nodes) {
    const root = find(n.id)
    if (!groups.has(root)) groups.set(root, [])
    groups.get(root).push(n)
  }
  return [...groups.values()].sort((a, b) => b.length - a.length)
}

/**
 * Build an indented forest for one set of nodes: roots are nodes with no
 * incoming edge (falling back to the highest-degree node when every node is in
 * a cycle). Each entry is `{ node, depth, viaCycle }`.
 */
export function forestRows(nodes, apiEdges) {
  const ids = new Set(nodes.map((n) => n.id))
  const edges = normalizeEdges(nodes, apiEdges).filter((e) => ids.has(e.from) && ids.has(e.to))
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const children = new Map(nodes.map((n) => [n.id, []]))
  const indeg = new Map(nodes.map((n) => [n.id, 0]))
  for (const e of edges) {
    children.get(e.from).push(e.to)
    indeg.set(e.to, indeg.get(e.to) + 1)
  }
  let roots = nodes.filter((n) => indeg.get(n.id) === 0).map((n) => n.id)
  if (!roots.length && nodes.length) {
    roots = [
      nodes
        .map((n) => ({ id: n.id, d: children.get(n.id).length }))
        .sort((a, b) => b.d - a.d)[0].id,
    ]
  }
  const rows = []
  const visited = new Set()
  const walk = (id, depth, path) => {
    const repeat = path.has(id)
    rows.push({ node: byId.get(id), depth, viaCycle: repeat })
    if (repeat || depth > 12) return
    visited.add(id)
    const next = new Set(path)
    next.add(id)
    for (const c of children.get(id)) walk(c, depth + 1, next)
  }
  for (const r of roots) walk(r, 0, new Set())
  for (const n of nodes) if (!visited.has(n.id)) rows.push({ node: n, depth: 0, viaCycle: false })
  return rows
}
